import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, FlaskConical, Search, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Btn, Chip, Empty, PageHeader, Spinner } from "@/components/rp";
import { NewClientDialog } from "@/components/admin/dialogs";

const InviteBtn = ({ c, onDone }) => {
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/admin/users/${c.id}/invite`); toast.success(`Invito reinviato a ${c.email}`); onDone(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <span className="flex items-center gap-2 shrink-0">
      <span className="hidden sm:inline text-xs text-[#9A6B00]" data-testid={`client-invite-pending-${c.id}`}>Password non ancora scelta</span>
      <Btn variant="outline" className="h-8 text-xs" loading={busy} onClick={send} data-testid={`client-invite-resend-${c.id}`}>Reinvia invito</Btn>
    </span>
  );
};

export default function AdminClienti() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("");
  const [open, setOpen] = useState(false);
  const [demo, setDemo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/clients").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const seed = async () => {
    if (!window.confirm("Creare (o ricreare da zero) il Cliente Prova con preventivo, cantiere, pratica, extra, varianti e chat? Non parte nessuna email.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/demo/cliente-prova"); setDemo(data); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const hasDemo = (list || []).some((c) => c.email === "cliente.prova@example.com");
  const unseed = async () => {
    if (!window.confirm("Eliminare il Cliente Prova con preventivo, cantiere, pratica, extra, varianti, foto, diario e messaggi? L'operazione non si può annullare.")) return;
    setBusy(true);
    try { await api.delete("/admin/demo/cliente-prova"); setDemo(null); load(); toast.success("Cliente Prova eliminato con tutti i suoi dati"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const s = q.trim().toLowerCase();
  const sources = [...new Set((list || []).map((c) => c.provenienza).filter(Boolean))];
  const shown = (list || []).filter((c) => (!src || c.provenienza === src) && (!s || [c.name, c.email, c.phone, c.provenienza].some((v) => (v || "").toLowerCase().includes(s))));

  return (
    <div>
      <PageHeader label="Clienti" title="I tuoi clienti">
        {hasDemo && <Btn variant="danger" loading={busy} onClick={unseed} data-testid="demo-client-delete-button"><Trash2 className="h-4 w-4" /> Elimina Cliente Prova</Btn>}
        <Btn variant="outline" loading={busy} onClick={seed} data-testid="demo-client-button"><FlaskConical className="h-4 w-4" /> Cliente di prova</Btn>
        <Btn onClick={() => setOpen(true)} data-testid="clients-new-button"><UserPlus className="h-4 w-4" /> Nuovo cliente</Btn>
      </PageHeader>
      {demo && (
        <div className="rounded-2xl bg-[#E6DDCE] p-4 text-sm mb-5" data-testid="demo-client-result">
          Cliente di prova pronto. Accesso cliente: <b>{demo.email}</b> · password <b data-testid="demo-client-password">{demo.password}</b> (si vede solo ora).{" "}
          <Link to={`/admin/commesse/${demo.order_id}`} className="underline" data-testid="demo-open-order">Apri il cantiere {demo.order_code}</Link> · <Link to={`/admin/preventivi/${demo.quote_id}`} className="underline" data-testid="demo-open-quote">Apri il preventivo</Link>
        </div>
      )}
      <div className="relative mb-5 max-w-md">
        <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#757575]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email o telefono" className="rp-field pl-11 rounded-full" data-testid="clients-search-input" />
      </div>
      {sources.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="clients-provenienza-filter">
          <Chip active={!src} onClick={() => setSrc("")} data-testid="clients-provenienza-all">Tutte le provenienze</Chip>
          {sources.map((p) => <Chip key={p} active={src === p} onClick={() => setSrc(p)} data-testid={`clients-provenienza-${p}`}>{p} ({list.filter((c) => c.provenienza === p).length})</Chip>)}
        </div>
      )}
      {!list ? <Spinner /> : !shown.length ? (
        <Empty icon={Users} title="Nessun cliente trovato" data-testid="clients-empty" />
      ) : (
        <div className="rp-card divide-y divide-[#EBEBEB] overflow-hidden" data-testid="clients-list">
          {shown.map((c) => (
            <Link key={c.id} to={`/admin/clienti/${c.id}`} data-testid={`client-row-${c.id}`} className="flex items-center gap-4 p-4 sm:px-6 hover:bg-white transition-colors">
              <span className="h-11 w-11 rounded-full bg-[#E6DDCE] flex items-center justify-center font-semibold shrink-0">{c.name.charAt(0).toUpperCase()}</span>
              <span className="flex-1 min-w-0">
                <span className="block font-medium truncate">{c.name}</span>
                <span className="block text-xs text-[#757575] truncate">{c.email}{c.phone && ` · ${c.phone}`} · {c.cantieri_count} cantier{c.cantieri_count === 1 ? "e" : "i"}{c.provenienza && ` · da ${c.provenienza}`}</span>
              </span>
              {c.unread > 0 && <span className="h-6 min-w-[24px] px-2 rounded-full bg-[#070707] text-white text-xs font-semibold flex items-center justify-center" data-testid={`client-unread-${c.id}`}>{c.unread}</span>}
              {c.invite_pending && <InviteBtn c={c} onDone={load} />}
              <ChevronRight className="h-4 w-4 text-[#757575]" />
            </Link>
          ))}
        </div>
      )}
      <NewClientDialog open={open} onClose={() => setOpen(false)} onSaved={(r) => navigate(`/admin/clienti/${r.client.id}`)} />
    </div>
  );
}
